import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import type { HealthResponse } from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'failed'; message: string; code: string };

/**
 * Connection check. The write button exists so the failure path is real and testable:
 * a save is only ever reported as saved when the server returned success (ADR-002).
 */
export function DiagnosticsPage(): JSX.Element {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });

  useEffect(() => {
    const controller = new AbortController();
    apiRequest<HealthResponse>('/api/health', { signal: controller.signal })
      .then((result) => setHealth(result))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setHealthError(error instanceof ApiClientError ? error.message : 'Health check failed.');
      });
    return () => controller.abort();
  }, []);

  const attemptWrite = useCallback(() => {
    setSave({ kind: 'saving' });
    apiRequest('/api/settings', { method: 'POST', body: { probe: true } })
      .then(() => setSave({ kind: 'saved' }))
      .catch((error: unknown) => {
        const failure =
          error instanceof ApiClientError
            ? { message: error.message, code: error.code }
            : { message: 'The save failed.', code: 'INTERNAL_ERROR' };
        setSave({ kind: 'failed', ...failure });
      });
  }, []);

  return (
    <section data-testid="diagnostics-page">
      <div className="card">
        <h2>API health</h2>
        {health ? (
          <p className="status status--ok" data-testid="health-ok">
            {health.app} {health.version} · {health.environment} ·{' '}
            {health.database.appliedMigrations} migrations applied
          </p>
        ) : null}
        {healthError ? (
          <p className="status status--error" data-testid="health-error">
            {healthError}
          </p>
        ) : null}
        {!health && !healthError ? <p className="meta">Checking…</p> : null}
      </div>

      <div className="card">
        <h2>Write check</h2>
        <p className="meta">
          Attempts a real write. InventoHub never stores a save locally and never reports success
          the server did not confirm.
        </p>
        <button
          type="button"
          className="button"
          onClick={attemptWrite}
          disabled={save.kind === 'saving'}
          data-testid="save-button"
        >
          {save.kind === 'saving' ? 'Saving…' : 'Attempt test write'}
        </button>
        {save.kind === 'saved' ? (
          <p className="status status--ok" data-testid="save-success">
            Saved.
          </p>
        ) : null}
        {save.kind === 'failed' ? (
          <p className="status status--error" data-testid="save-error">
            Not saved — {save.message} ({save.code})
          </p>
        ) : null}
      </div>
    </section>
  );
}
